import { Injectable, TemplateRef, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import Swal, { SweetAlertIcon, SweetAlertResult } from 'sweetalert2';
import { ErrorBannerService } from './error-banner.service';

// Create a dedicated toast instance that won't interfere with modals
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'danger' | 'info' | 'success';
  showCancel?: boolean;
}

export interface DeleteConfirmOptions {
  itemName: string;
  customMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmationService {
  private readonly translate = inject(TranslateService);
  private readonly errorBanner = inject(ErrorBannerService);

  constructor(private modalService: NgbModal) {}

  /**
   * Store the current confirmation data for template access
   */
  private confirmationData = {
    title: '',
    message: '',
    confirmText: 'Okay',
    cancelText: 'Cancel'
  };

  /**
   * Get confirmation data for template
   */
  getConfirmationData(): { title: string; message: string; confirmText: string; cancelText: string } {
    return this.confirmationData;
  }

  /**
   * Show confirmation dialog using a template
   */
  confirm(template: TemplateRef<unknown>, title: string, message: string, confirmText = 'Okay', cancelText = 'Cancel'): Promise<boolean> {
    this.confirmationData.title = title;
    this.confirmationData.message = message;
    this.confirmationData.confirmText = confirmText;
    this.confirmationData.cancelText = cancelText;

    const modalRef = this.modalService.open(template, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      windowClass: 'deleteModalOpen'
    });

    return modalRef.result.then(
      (result) => result === 'confirm',
      () => false
    );
  }

  /**
   * Show save confirmation dialog
   */
  confirmSave(template: TemplateRef<unknown>, customMessage?: string): Promise<boolean> {
    return this.confirm(
      template,
      this.translate.instant('confirmation.save_title'),
      customMessage || this.translate.instant('confirmation.save_message'),
      this.translate.instant('common.save'),
      this.translate.instant('common.cancel')
    );
  }

  /**
   * Show bulk update confirmation dialog
   */
  confirmBulkUpdate(template: TemplateRef<unknown>, count: number, customMessage?: string): Promise<boolean> {
    return this.confirm(
      template,
      this.translate.instant('confirmation.bulk_update_title'),
      customMessage || this.translate.instant('confirmation.bulk_update_message', { count }),
      this.translate.instant('confirmation.bulk_update_confirm'),
      this.translate.instant('common.cancel')
    );
  }

  // ============================================
  // SweetAlert2-based methods (no template required)
  // ============================================

  private getIconForType(type: ConfirmOptions['type']): SweetAlertIcon {
    const iconMap: Record<string, SweetAlertIcon> = {
      warning: 'warning',
      danger: 'error',
      info: 'info',
      success: 'success'
    };
    return iconMap[type || 'warning'];
  }

  private getButtonColorForType(type: ConfirmOptions['type']): string {
    const colorMap: Record<string, string> = {
      warning: '#f7b84b',
      danger: '#f06548',
      info: '#299cdb',
      success: '#007D00'
    };
    return colorMap[type || 'warning'];
  }

  /**
   * Show a SweetAlert2 confirmation dialog (no template required)
   *
   * Usage:
   * ```typescript
   * const confirmed = await this.confirmationService.swalConfirm({
   *   title: 'Delete Room',
   *   message: 'Are you sure you want to delete this room?',
   *   type: 'danger',
   *   confirmText: 'Delete'
   * });
   * if (confirmed) { ... }
   * ```
   */
  async swalConfirm(options: ConfirmOptions): Promise<boolean> {
    const result: SweetAlertResult = await Swal.fire({
      title: options.title,
      text: options.message,
      icon: this.getIconForType(options.type),
      showCancelButton: options.showCancel !== false,
      confirmButtonColor: this.getButtonColorForType(options.type),
      cancelButtonColor: '#878a99',
      confirmButtonText: options.confirmText || this.translate.instant('common.confirm'),
      cancelButtonText: options.cancelText || this.translate.instant('common.cancel'),
      reverseButtons: true,
      customClass: {
        confirmButton: 'swal2-confirm-btn',
        cancelButton: 'swal2-cancel-btn'
      }
    });

    return result.isConfirmed;
  }

  /**
   * Show a delete confirmation dialog (convenience method)
   *
   * Usage:
   * ```typescript
   * const confirmed = await this.confirmationService.swalConfirmDelete('Room 101');
   * if (confirmed) { this.deleteRoom(); }
   * ```
   */
  async swalConfirmDelete(itemName: string, customMessage?: string): Promise<boolean> {
    const message = customMessage ||
      this.translate.instant('common.confirm_delete_message', { item: itemName }) ||
      `Are you sure you want to delete "${itemName}"? This action cannot be undone.`;

    return this.swalConfirm({
      title: this.translate.instant('common.confirm_delete'),
      message,
      type: 'danger',
      confirmText: this.translate.instant('common.delete')
    });
  }

  /**
   * Show a success toast notification
   */
  swalSuccess(title: string, message?: string): void {
    Toast.fire({
      title,
      text: message,
      icon: 'success',
      iconColor: '#007D00',
      timer: 2500
    });
  }

  /**
   * Show an inline error banner (persistent, dismissible, clears on navigation).
   */
  swalError(title: string, message?: string): void {
    const fullMessage = message ? `${title} ${message}` : title;
    this.errorBanner.show(fullMessage);
  }

  /**
   * Dismiss the error banner — call this on successful operations so the
   * previous error no longer shows alongside the success message.
   */
  clearError(): void {
    this.errorBanner.clear();
  }

  /**
   * Show a warning toast notification
   */
  swalWarning(title: string, message?: string): void {
    Toast.fire({
      title,
      text: message,
      icon: 'warning',
      timer: 3000
    });
  }

  /**
   * Show an info toast notification
   */
  swalInfo(title: string, message?: string): void {
    Toast.fire({
      title,
      text: message,
      icon: 'info',
      timer: 3000
    });
  }
}